// frontend/src/components/formularios/campos/TablaEvaluacionMensual.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useTheme,
  useMediaQuery,
  MenuItem  
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  AssignmentTurnedIn as AssignmentIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import es from 'date-fns/locale/es';
import SeleccionObjetivoPadre from './SeleccionObjetivoPadre';
import CampoModal from './CampoModal';
import formularioService from '../../../services/formularioService';
import { format, addDays } from 'date-fns';

const TablaEvaluacionMensual = ({ 
  campo, 
  value = {}, 
  onChange,
  parentRespuestaId,
  disabled = false 
}) => {
  const { configuracion } = campo;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [objetivosDisponibles, setObjetivosDisponibles] = useState([]);
  const [objetivosSeleccionados, setObjetivosSeleccionados] = useState(
    value?.objetivos_seleccionados || []
  );
  const [evaluaciones, setEvaluaciones] = useState(value?.evaluaciones || {});
  const [mesTrabajo, setMesTrabajo] = useState(value?.mes_trabajo || new Date().toISOString().split('T')[0]);
  const [paso, setPaso] = useState(
    value?.objetivos_seleccionados && value.objetivos_seleccionados.length > 0 ? 1 : 0
  );

  // Estados de bloqueo (MANTENER para neuropsicología)
  const [objetivosBloqueados, setObjetivosBloqueados] = useState(value?.objetivos_bloqueados || {});
  const [formularioBloqueado, setFormularioBloqueado] = useState(value?.estado === 'cerrado');
  
  useEffect(() => {
    if (parentRespuestaId) {
      cargarObjetivos();
    }
  }, [parentRespuestaId]);
  
  useEffect(() => {
    // Inicializar objetivos seleccionados desde el valor guardado
    if (value && Object.keys(value).length > 0) {
      if (value.objetivos_seleccionados !== undefined) {
        setObjetivosSeleccionados(value.objetivos_seleccionados);
        if (value.objetivos_seleccionados.length > 0) {
          setPaso(1);
        }
      }
      if (value.evaluaciones !== undefined) {
        setEvaluaciones(value.evaluaciones);
      }
      if (value.objetivos_bloqueados !== undefined) {
        setObjetivosBloqueados(value.objetivos_bloqueados);
      }
      if (value.mes_trabajo !== undefined) {
        setMesTrabajo(value.mes_trabajo);
      }
      if (value.estado === 'cerrado') {
        setFormularioBloqueado(true);
      }
    }
  }, [value]);
  
  const cargarObjetivos = async () => {
    try {
      setLoading(true);
      const response = await formularioService.getObjetivosDisponibles(parentRespuestaId);
      
      if (response.success) {
        setObjetivosDisponibles(response.data.objetivos_disponibles || []);
      }
    } catch (error) {
      console.error('Error al cargar objetivos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Verificar si un objetivo debe bloquearse (MANTENER reglas de bloqueo)
  const verificarBloqueoObjetivo = (objetivoId) => {
    const evaluacion = evaluaciones[objetivoId];
    if (!evaluacion) return { bloqueado: false };

    // Calcular promedio
    const puntajes = [];
    if (evaluacion.ses1_puntaje) puntajes.push(parseFloat(evaluacion.ses1_puntaje));
    if (evaluacion.ses2_puntaje) puntajes.push(parseFloat(evaluacion.ses2_puntaje));
    
    const promedio = puntajes.length > 0 ? 
      puntajes.reduce((a, b) => a + b, 0) / puntajes.length : 0;

    // Si el promedio es >= 3, está logrado
    if (promedio >= 3) {
      return {
        bloqueado: true,
        razon: 'Objetivo logrado (promedio >= 3)',
        requiereAnalisis: false
      };
    }

    return { bloqueado: false };
  };

  // Actualizar valor completo
  const actualizarValor = (nuevosObjetivos = null, nuevasEvaluaciones = null, nuevosBloqueos = null) => {
    const nuevoValor = {
      mes_trabajo: mesTrabajo,
      objetivos_seleccionados: nuevosObjetivos !== null ? nuevosObjetivos : objetivosSeleccionados,
      evaluaciones: nuevasEvaluaciones !== null ? nuevasEvaluaciones : evaluaciones,
      objetivos_bloqueados: nuevosBloqueos !== null ? nuevosBloqueos : objetivosBloqueados,
      estado: formularioBloqueado ? 'cerrado' : 'abierto'
    };

    onChange(nuevoValor);
  };
  
  // Manejar selección de objetivos con bloqueo
  const handleSeleccionarObjetivos = (objetivos) => {
   
    setObjetivosSeleccionados(objetivos);
    
    // Inicializar evaluaciones vacías para nuevos objetivos
    const nuevasEvaluaciones = { ...evaluaciones };
      objetivos.forEach(objId => {
        if (!nuevasEvaluaciones[objId]) {
          nuevasEvaluaciones[objId] = {
            fecha_sesion: null,       
            puntaje1: '',            
            puntaje2: '',             
            promedio: 0,
            observacion: '',
            notas_detalladas: ''
          };
        }
      });
    setEvaluaciones(nuevasEvaluaciones);
    
    // BLOQUEAR selección y avanzar al paso 1
    if (objetivos.length > 0) {
      setPaso(1);
     }
    
    // Actualizar valor completo
    actualizarValor(objetivos, nuevasEvaluaciones);
  };
  
  const handleCambioEvaluacion = (objetivoId, campo, valor) => {
    const evaluacionActual = evaluaciones[objetivoId] || {};
    const nuevaEvaluacion = {
      ...evaluacionActual,
      [campo]: valor
    };
    
    // Calcular promedio si ambos puntajes están presentes
    if (campo === 'puntaje1' || campo === 'puntaje2') {
      const p1 = campo === 'puntaje1' ? parseFloat(valor) || 0 : parseFloat(nuevaEvaluacion.puntaje1) || 0;
      const p2 = campo === 'puntaje2' ? parseFloat(valor) || 0 : parseFloat(nuevaEvaluacion.puntaje2) || 0;
      
      if (p1 > 0 && p2 > 0) {
        nuevaEvaluacion.promedio = (p1 + p2) / 2;
        
        // Determinar observación según promedio
        if (nuevaEvaluacion.promedio >= 3) {
          nuevaEvaluacion.observacion = 'Logrado';
        } else if (nuevaEvaluacion.promedio >= 2) {
          nuevaEvaluacion.observacion = 'En Proceso';
        } else {
          nuevaEvaluacion.observacion = 'Reprogramación';
        }
      }
    }
    
    const nuevasEvaluaciones = {
      ...evaluaciones,
      [objetivoId]: nuevaEvaluacion
    };
    
    setEvaluaciones(nuevasEvaluaciones);
    actualizarValor(null, nuevasEvaluaciones);
  };
  
  const validarPuntaje = (valor) => {
    const num = parseFloat(valor);
    return !isNaN(num) && num >= 1 && num <= 3;
  };
  
  const getColorObservacion = (observacion) => {
    switch (observacion) {
      case 'Logrado': return 'success';
      case 'En Proceso': return 'warning';
      case 'Reprogramación': return 'error';
      default: return 'default';
    }
  };
  
  const getIconObservacion = (observacion) => {
    switch (observacion) {
      case 'Logrado': return <CheckIcon />;
      case 'En Proceso': return <WarningIcon />;
      case 'Reprogramación': return <ErrorIcon />;
      default: return null;
    }
  };
  
  const parseValidDate = (dateValue) => {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  };

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    let logrados = 0;
    let enProceso = 0;
    let bloqueados = 0;
    
    objetivosSeleccionados.forEach(objId => {
      if (objetivosBloqueados[objId]) {
        bloqueados++;
      } else {
        const evaluacion = evaluaciones[objId] || {};
        if (evaluacion.observacion === 'Logrado') logrados++;
        else if (evaluacion.observacion === 'En Proceso') enProceso++;
      }
    });
    
    return { logrados, enProceso, bloqueados, total: objetivosSeleccionados.length };
  };

  const stats = calcularEstadisticas();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box>
        {/* Header con información del mes */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6" color="primary">
                  Evaluación Mensual Neuropsicológica
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Período: {format(new Date(mesTrabajo), 'dd MMMM', { locale: es })} al {format(addDays(new Date(mesTrabajo), 30), 'dd MMMM yyyy', { locale: es })} (30 días)
                </Typography>
              </Box>
              
              {objetivosSeleccionados.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip 
                    icon={<CheckIcon />}
                    label={`${stats.logrados} logrados`}
                    color="success"
                    size="small"
                  />
                  <Chip 
                    icon={<WarningIcon />}
                    label={`${stats.enProceso} en proceso`}
                    color="warning"
                    size="small"
                  />
                  <Chip 
                    icon={<LockIcon />}
                    label={`${stats.bloqueados} bloqueados`}
                    color="error"
                    size="small"
                  />
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Stepper para el flujo de trabajo */}
        <Stepper activeStep={paso} orientation="vertical" sx={{ mb: 3 }}>
          <Step>
            <StepLabel>
              Seleccionar objetivos para evaluar
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Seleccione los objetivos específicos que evaluará durante {format(new Date(mesTrabajo), 'MMMM yyyy', { locale: es })}:
                </Typography>
              </Box>
              
              <SeleccionObjetivoPadre
                campo={{
                  ...campo,
                  etiqueta: 'Objetivos a evaluar en este período'
                }}
                value={objetivosSeleccionados}
                onChange={(nuevosObjetivos) => {
                  // SOLO actualizar estado local, NO avanzar de paso
                  setObjetivosSeleccionados(nuevosObjetivos);
                  
                  // Inicializar evaluaciones para objetivos nuevos
                  const nuevasEvaluaciones = { ...evaluaciones };
                  nuevosObjetivos.forEach(objId => {
                    if (!nuevasEvaluaciones[objId]) {
                      nuevasEvaluaciones[objId] = {
                        ses1_fecha: null,
                        ses1_puntaje: '',
                        ses2_fecha: null,
                        ses2_puntaje: '',
                        promedio: 0,
                        observacion: '',
                        notas_detalladas: ''
                      };
                    }
                  });
                  setEvaluaciones(nuevasEvaluaciones);
                  
                 }}
                parentRespuestaId={parentRespuestaId}
                disabled={disabled || formularioBloqueado}
                multiselect={true}
                maxSelecciones={configuracion.max_objetivos_mes || 15}
                mostrarEstado={true}
                modoVisualizacion="lista"
              />
              
              {objetivosSeleccionados.length > 0 && (
                <Box mt={3}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>¿Confirmar selección?</strong><br/>
                      Ha seleccionado <strong>{objetivosSeleccionados.length} objetivos</strong> para evaluar durante este período.
                      Una vez bloqueada la selección, no podrá modificar los objetivos hasta el siguiente período.
                    </Typography>
                  </Alert>
                  
                  <Button 
                    variant="contained" 
                    size="large"
                    fullWidth
                    onClick={() => {
                      // Guardar y bloquear selección
                      actualizarValor(objetivosSeleccionados, evaluaciones);
                      setPaso(1);
                     }}
                    startIcon={<LockIcon />}
                    color="error"
                    sx={{
                      py: 1.5,
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}
                  >
                    🔒 BLOQUEAR SELECCIÓN Y COMENZAR EVALUACIÓN
                  </Button>
                </Box>
              )}
            </StepContent>
          </Step>
          
          <Step>
            <StepLabel>
              Registrar evaluaciones de sesiones
            </StepLabel>
            <StepContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Objetivos seleccionados: <strong>{objetivosSeleccionados.length}</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  A continuación puede registrar las evaluaciones para cada objetivo seleccionado.
                  La selección de objetivos está bloqueada hasta completar este mes.
                </Typography>
              </Box>

 {/* Leyenda */}
        {configuracion.leyenda && (
          <Box mt={2} p={2} component={Paper} variant="outlined">
            <Typography variant="subtitle2" gutterBottom>
              {configuracion.leyenda.titulo}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              {configuracion.leyenda.items.map((item, idx) => (
                <Chip
                  key={idx}
                  label={`${item.valor || item.codigo}: ${item.descripcion}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        
              {/* Tabla de evaluación RESPONSIVA */}
              {objetivosSeleccionados.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell width="30%">Objetivo Específico</TableCell>
                        <TableCell width="12%">Fecha Sesión</TableCell>
                        <TableCell width="10%">Puntaje 1</TableCell>
                        <TableCell width="10%">Puntaje 2</TableCell>
                        <TableCell width="8%">Promedio</TableCell>
                        <TableCell width="15%">Estado</TableCell>
                        <TableCell width="15%">Notas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {objetivosSeleccionados.map((objId) => {
                        const objetivo = objetivosDisponibles.find(o => o.id === objId);
                        const evaluacion = evaluaciones[objId] || {};
                        const estaBloqueado = objetivosBloqueados[objId];
                        
                        return (
                          <TableRow key={objId} sx={{ bgcolor: estaBloqueado ? 'error.50' : 'transparent' }}>
                            <TableCell>
                              <Box>
                                {objetivo ? (
                                  <>
                                    <Typography variant="body2" fontWeight="bold" color="primary">
                                      📋 {objetivo.area_nombre}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Obj. {objetivo.objetivo_numero}:</strong> {objetivo.objetivo_texto}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="body2" color="error">
                                    ⚠️ Objetivo ID: {objId}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            
                            {/* Una sola fecha para la sesión */}
                            <TableCell>
                              <DatePicker
                                value={parseValidDate(evaluacion.fecha_sesion)}
                                onChange={(newValue) => handleCambioEvaluacion(objId, 'fecha_sesion', newValue)}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    fullWidth: true
                                  }
                                }}
                                disabled={disabled || estaBloqueado}
                              />
                            </TableCell>
                            
                            {/* Puntaje 1 */}
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                value={evaluacion.puntaje1 || ''}
                                onChange={(e) => handleCambioEvaluacion(objId, 'puntaje1', e.target.value)}
                                disabled={disabled || estaBloqueado}
                                fullWidth
                              >
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="2">2</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                              </TextField>
                            </TableCell>
                            
                            {/* Puntaje 2 */}
                            <TableCell>
                              <TextField
                                select
                                size="small"
                                value={evaluacion.puntaje2 || ''}
                                onChange={(e) => handleCambioEvaluacion(objId, 'puntaje2', e.target.value)}
                                disabled={disabled || estaBloqueado}
                                fullWidth
                              >
                                <MenuItem value="">-</MenuItem>
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="2">2</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                              </TextField>
                            </TableCell>
                            
                            {/* Promedio calculado */}
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="bold">
                                {evaluacion.promedio ? evaluacion.promedio.toFixed(1) : '-'}
                              </Typography>
                            </TableCell>
                            
                            {/* Estado según promedio */}
                            <TableCell>
                              {evaluacion.observacion && (
                                <Chip
                                  icon={getIconObservacion(evaluacion.observacion)}
                                  label={evaluacion.observacion}
                                  color={getColorObservacion(evaluacion.observacion)}
                                  size="small"
                                />
                              )}
                            </TableCell>
                            
                            {/* Notas detalladas */}
                            <TableCell>
                              <CampoModal
                                value={evaluacion.notas_detalladas || ''}
                                onChange={(newValue) => handleCambioEvaluacion(objId, 'notas_detalladas', newValue)}
                                label={`Notas - ${objetivo?.area_nombre || 'Objetivo'}`}
                                placeholder="Observaciones"
                                disabled={disabled || estaBloqueado}
                                minHeight={250}
                                sx={{ minHeight: '40px', maxWidth: isMobile ? '120px' : '200px' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No se han seleccionado objetivos para evaluar este mes
                </Alert>
              )}

              {/* Botones de acción para el paso */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Button 
                  onClick={() => setPaso(0)}
                  disabled={formularioBloqueado}
                  startIcon={<EditIcon />}
                >
                  Cambiar selección
                </Button>
                
                <Alert severity="info" sx={{ flex: 1, mx: 2, minWidth: '200px' }}>
                  <Typography variant="caption">
                    Puede continuar evaluando durante todo el mes de {format(new Date(mesTrabajo), 'MMMM yyyy', { locale: es })}
                  </Typography>
                </Alert>
                
                <Button 
                  variant="contained" 
                  disabled={Object.keys(evaluaciones).length === 0}
                  startIcon={<AssignmentIcon />}
                >
                  Ver estadísticas
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
        
       
      </Box>
    </LocalizationProvider>
  );
};

export default TablaEvaluacionMensual;